import type { Clef, NEvent, NPitch, Notation } from '../../music/notation';
import {
  ACC_W,
  HEAD_W,
  WHOLE_W,
  accidental,
  beam as beamPath,
  blackHead,
  circle,
  cClef,
  fermata as fermataPath,
  flag as flagPath,
  halfHead,
  halfRest,
  hookRest,
  poly,
  quarterRest,
  rect,
  tie as tiePath,
  wholeHead,
  wholeRest,
  BASS_CLEF,
  BASS_DOTS,
  TREBLE_BALL,
  TREBLE_CLEF,
} from './glyphs';

/**
 * Engraving of a whole piece as one continuous system, in staff spaces. Spacing follows the
 * written durations (about ×1.5 per doubling of the value, as in engraved scores), with room
 * for accidentals, dots, flags, grace notes and barlines; beams, stems, ties and accidentals
 * follow the usual rules (Gould, "Behind Bars"), taking stems and beams from the edition where
 * it gives them. Drawing is split into chunks along the system so that a moving view only draws
 * what is on screen.
 */

export const STAFF_GAP = 6.5; // from the bottom line of one staff to the top line of the next
export const MARGIN_TOP = 4.4;
export const MARGIN_BOTTOM = 4.2;
export const CHUNK = 40;

export interface Chunk {
  fill: string[];
  eo: string[];
  hair: string[];
  /** Raw SVG (scaled glyph groups). */
  raw: string[];
  text: Array<{ x: number; y: number; s: string; cls: string }>;
}

export interface HeadMark {
  t: number;
  end: number;
  d: string;
  eo: boolean;
}

export interface Engraving {
  width: number;
  height: number;
  staves: number;
  staffTop: number[];
  /** Note columns: time (quarters) and x (centre of the noteheads), for the playhead. */
  cols: Array<{ t: number; x: number }>;
  chunks: Map<number, Chunk>;
  heads: HeadMark[];
  clefAt: (staff: number, t: number) => Clef;
  keyAt: (t: number) => number;
  end: number;
}

/** Step of the bottom line for each clef (C4 = 28). */
const BOTTOM: Record<Clef, number> = { G: 30, G8: 23, F: 18, C3: 24, C4: 22 };
const SHARPS = [3, 0, 4, 1, 5, 2, 6];
const FLATS = [6, 2, 5, 1, 4, 0, 3];
/** Key-signature steps on the treble staff (sharps F5 C5 G5 D5 A4 E5 B4; flats B4 E5 A4 D5 G4 C5 F4). */
const SHARP_STEPS = [38, 35, 39, 36, 33, 37, 34];
const FLAT_STEPS = [34, 37, 33, 36, 32, 35, 31];

export function keySignatureSteps(fifths: number, clef: Clef): Array<[number, number]> {
  const shift = clef === 'G' ? 0 : clef === 'F' ? -14 : clef === 'C4' ? -9 : -7;
  const out: Array<[number, number]> = [];
  if (fifths > 0) for (let i = 0; i < fifths; i++) out.push([SHARP_STEPS[i] + shift, 1]);
  if (fifths < 0) for (let i = 0; i < -fifths; i++) out.push([FLAT_STEPS[i] + shift, -1]);
  return out;
}

function spacing(d: number): number {
  return Math.max(2.1, 5.0 * Math.pow(Math.max(d, 1e-3), 0.6));
}

const valueFlags: Record<string, number> = { e: 1, s: 2, t: 3, x: 4, o: 5 };

interface Info {
  e: NEvent;
  ys: number[];
  accs: Array<number | null>;
  cont: boolean[];
  stem: number; // -1 up, 1 down, 0 none
  x: number; // head column (left edge of normal-side heads)
  shift: number;
  side: number[]; // per pitch: 0 normal, 1 right of stem (up), -1 left of stem (down)
  tip: number; // y of stem tip
  stemX: number;
  beamed: boolean;
  multi: boolean;
}

/**
 * Engrave once with the default distance between the staves, measure how far each staff's
 * notation reaches into the space between them (stems, beams, ledger notes, tuplet numbers),
 * then engrave again with a gap that clears it — one gap for the whole piece, so the moving
 * score keeps its height.
 */
export function engrave(n: Notation, opts: { satb: boolean }): Engraving {
  const first = engraveWith(n, opts, STAFF_GAP);
  if (first.staves < 2) return first;
  const below = first.reach.below; // how far staff 1 reaches under its bottom line
  const above = first.reach.above; // how far staff 2 reaches over its top line
  const gap = Math.min(12, Math.max(STAFF_GAP, below + above + 1.5));
  return gap > STAFF_GAP + 0.25 ? engraveWith(n, opts, gap) : first;
}

function engraveWith(n: Notation, opts: { satb: boolean }, staffGap: number): Engraving & { reach: { below: number; above: number } } {
  const staves = Math.max(1, n.staves);
  const staffTop = Array.from({ length: staves }, (_, i) => MARGIN_TOP + i * (4 + staffGap));
  // How far into the gap the upper staff reaches below its bottom line, and the lower one above its top line.
  const reach = { below: 0, above: 0 };
  const track = (staff: number, y: number) => {
    if (staves < 2) return;
    if (staff === 1) reach.below = Math.max(reach.below, y - (staffTop[0] + 4));
    else if (staff === 2) reach.above = Math.max(reach.above, staffTop[1] - y);
  };
  const height = staffTop[staves - 1] + 4 + MARGIN_BOTTOM;
  const last = n.measures[n.measures.length - 1];
  const end = last ? last.start + last.length : 0;

  /* ---------- Context: clef, key, time ---------- */
  const clefs = [...n.clefs].sort((a, b) => a.t - b.t);
  const clefAt = (staff: number, t: number): Clef => {
    let c: Clef = staff === 1 ? 'G' : 'F';
    let found = false;
    for (const x of clefs) {
      if (x.staff !== staff) continue;
      if (x.t <= t + 1e-6 || !found) {
        if (x.t <= t + 1e-6 || !found) c = x.clef;
        found = true;
        if (x.t > t + 1e-6) break;
      }
    }
    return opts.satb ? (staff === 1 ? 'G' : 'F') : c;
  };
  const keys = [...n.keys].sort((a, b) => a.t - b.t);
  const keyAt = (t: number) => {
    let k = keys.length ? keys[0].fifths : 0;
    for (const x of keys) if (x.t <= t + 1e-6) k = x.fifths;
    return k;
  };
  const yOf = (staff: number, step: number, clef: Clef) => staffTop[staff - 1] + 4 - (step - BOTTOM[clef]) / 2;
  const mid = (staff: number) => staffTop[staff - 1] + 2;



  /* ---------- Per event: heights, accidentals, ties in ---------- */
  const streams = new Map<string, NEvent[]>();
  for (const e of n.events) {
    const k = `${e.staff}.${e.voice}`;
    const list = streams.get(k) ?? [];
    list.push(e);
    streams.set(k, list);
  }
  const infos = new Map<NEvent, Info>();
  for (const e of n.events) {
    const clef = clefAt(e.staff, e.t);
    infos.set(e, {
      e,
      ys: e.pitches.map((p) => yOf(e.staff, p.step, clef)),
      accs: e.pitches.map(() => null),
      cont: e.pitches.map(() => false),
      stem: 0,
      x: 0,
      shift: 0,
      side: e.pitches.map(() => 0),
      tip: 0,
      stemX: 0,
      beamed: false,
      multi: false,
    });
  }
  // Tie continuations.
  for (const list of streams.values()) {
    const open = new Map<number, number>(); // midi -> time the tie lands
    for (const e of list) {
      if (e.grace) continue;
      const inf = infos.get(e)!;
      e.pitches.forEach((p, i) => {
        const at = open.get(p.midi);
        if (at !== undefined && Math.abs(at - e.t) < 1e-3) inf.cont[i] = true;
      });
      open.clear();
      e.pitches.forEach((p) => {
        if (p.tie) open.set(p.midi, e.t + e.dur);
      });
    }
  }
  // Accidentals: by bar, staff and line/space; a tie into a new bar shows none and does not count.
  {
    const byMeasureStaff = new Map<string, NEvent[]>();
    for (const e of n.events) {
      if (e.rest) continue;
      const k = `${e.m}|${e.staff}`;
      const list = byMeasureStaff.get(k) ?? [];
      list.push(e);
      byMeasureStaff.set(k, list);
    }
    for (const list of byMeasureStaff.values()) {
      list.sort((a, b) => a.t - b.t || Number(b.grace) - Number(a.grace));
      const memory = new Map<number, number>();
      for (const e of list) {
        const fifths = keyAt(e.t);
        const keyAlter = new Array(7).fill(0);
        if (fifths > 0) for (let i = 0; i < fifths; i++) keyAlter[SHARPS[i]] = 1;
        if (fifths < 0) for (let i = 0; i < -fifths; i++) keyAlter[FLATS[i]] = -1;
        const inf = infos.get(e)!;
        e.pitches.forEach((p, i) => {
          const expected = memory.get(p.step) ?? keyAlter[p.letter];
          if (inf.cont[i]) {
            if (e.t > n.measures[e.m].start + 1e-6 || p.alter === expected) memory.set(p.step, p.alter);
            return;
          }
          if (p.alter !== expected) inf.accs[i] = p.alter;
          memory.set(p.step, p.alter);
        });
      }
    }
  }

  /* ---------- Voices per staff and bar: stems by voice when two voices share a staff ---------- */
  const voicesIn = new Map<string, Set<number>>();
  for (const e of n.events) {
    if (e.rest || e.grace) continue;
    const k = `${e.m}|${e.staff}`;
    const set = voicesIn.get(k) ?? new Set<number>();
    set.add(e.voice);
    voicesIn.set(k, set);
  }
  const multiVoice = (e: NEvent) => (voicesIn.get(`${e.m}|${e.staff}`)?.size ?? 0) > 1;
  const restVoices = new Map<string, Set<number>>();
  for (const e of n.events) {
    if (e.grace) continue;
    const k = `${e.m}|${e.staff}`;
    const set = restVoices.get(k) ?? new Set<number>();
    set.add(e.voice);
    restVoices.set(k, set);
  }

  // Beam groups from the edition.
  const groups: NEvent[][] = [];
  const groupOf = new Map<NEvent, NEvent[]>();
  for (const list of streams.values()) {
    let cur: NEvent[] | null = null;
    for (const e of list) {
      if (e.grace) continue;
      const b = e.beams[0];
      if (e.rest) {
        if (cur) cur.push(e);
        continue;
      }
      if (b === 'b') {
        cur = [e];
      } else if ((b === 'c' || b === 'e') && cur) {
        cur.push(e);
        if (b === 'e') {
          const notes = cur.filter((x) => !x.rest);
          // Trailing rests inside a group are not part of it.
          while (cur.length && cur[cur.length - 1].rest) cur.pop();
          if (notes.length >= 2) {
            groups.push(cur);
            for (const x of cur) groupOf.set(x, cur);
          }
          cur = null;
        }
      } else {
        cur = null;
      }
    }
  }

  const farthestDir = (staff: number, ys: number[]) => {
    const m = mid(staff);
    let best = 0;
    let dir = 1;
    for (const y of ys) {
      const d = Math.abs(y - m);
      if (d > best + 1e-6) {
        best = d;
        dir = y > m ? -1 : 1;
      }
    }
    return best < 1e-6 ? 1 : dir;
  };

  for (const e of n.events) {
    const inf = infos.get(e)!;
    if (e.rest || !e.pitches.length) continue;
    inf.multi = multiVoice(e);
    if (e.value === 'w' || e.value === 'W') {
      inf.stem = 0;
      continue;
    }
    if (e.grace) {
      inf.stem = -1;
      continue;
    }
    if (opts.satb) inf.stem = e.voice % 2 === 1 ? -1 : 1;
    else if (inf.multi) inf.stem = e.stem || (e.voice % 2 === 1 ? -1 : 1);
    else inf.stem = e.stem || farthestDir(e.staff, inf.ys);
  }
  // A beamed group takes one direction.
  for (const g of groups) {
    const notes = g.filter((x) => !x.rest);
    const given = notes.map((x) => infos.get(x)!.stem);
    let dir: number;
    if (opts.satb || notes.some((x) => infos.get(x)!.multi) || notes.some((x) => x.stem)) {
      const up = given.filter((d) => d < 0).length;
      dir = up * 2 >= given.length ? -1 : 1;
    } else {
      const staff = notes[0].staff;
      dir = farthestDir(staff, notes.flatMap((x) => infos.get(x)!.ys));
    }
    for (const x of notes) {
      infos.get(x)!.stem = dir;
      infos.get(x)!.beamed = true;
    }
  }

  // Seconds in a chord: heads on both sides of the stem.
  for (const inf of infos.values()) {
    const e = inf.e;
    if (e.rest || e.pitches.length < 2) continue;
    const order = e.pitches.map((_, i) => i).sort((a, b) => e.pitches[a].step - e.pitches[b].step);
    if (inf.stem <= 0) {
      let prevSide = 1;
      let prevStep = -99;
      for (const i of order) {
        const s = e.pitches[i].step;
        inf.side[i] = s - prevStep === 1 && prevSide === 0 ? 1 : 0;
        prevSide = inf.side[i];
        prevStep = s;
      }
    } else {
      let prevSide = 1;
      let prevStep = 999;
      for (const i of [...order].reverse()) {
        const s = e.pitches[i].step;
        inf.side[i] = prevStep - s === 1 && prevSide === 0 ? -1 : 0;
        prevSide = inf.side[i];
        prevStep = s;
      }
    }
  }

  /* ---------- Horizontal layout ---------- */
  const graceBefore = new Map<string, NEvent[]>(); // `${t}|${stream}` -> graces
  for (const list of streams.values()) {
    let pending: NEvent[] = [];
    for (const e of list) {
      if (e.grace) pending.push(e);
      else if (pending.length) {
        graceBefore.set(`${e.t.toFixed(4)}|${e.staff}.${e.voice}`, pending);
        pending = [];
      }
    }
  }
  const byTime = new Map<string, NEvent[]>();
  for (const e of n.events) {
    if (e.grace) continue;
    const k = e.t.toFixed(4);
    const list = byTime.get(k) ?? [];
    list.push(e);
    byTime.set(k, list);
  }
  const measureStarts = new Set(n.measures.map((m) => m.start.toFixed(4)));
  const times = [...new Set([...byTime.keys(), ...measureStarts, end.toFixed(4)])].map(Number).sort((a, b) => a - b);
  const keyChanges = new Map(keys.filter((k, i) => i > 0 && k.fifths !== keys[i - 1].fifths).map((k) => [k.t.toFixed(4), k]));
  const timeChanges = new Map(n.times.map((x) => [x.t.toFixed(4), x]));
  const clefChanges = new Map<string, Array<{ staff: number; clef: Clef }>>();
  if (!opts.satb) {
    const seen = new Map<number, Clef>();
    for (const c of clefs) {
      const prev = seen.get(c.staff);
      seen.set(c.staff, c.clef);
      if (prev === undefined || prev === c.clef) continue;
      const k = c.t.toFixed(4);
      const list = clefChanges.get(k) ?? [];
      list.push({ staff: c.staff, clef: c.clef });
      clefChanges.set(k, list);
    }
  }

  const timeSigW = (v: string) => (v === 'C' || v === 'C|' ? 2.0 : Math.max(...v.split('/').map((d) => d.length)) * 1.7 + 0.3);
  const keySigW = (from: number, to: number) => (to === 0 ? Math.abs(from) * 0.8 : Math.abs(to) * 1.0) + 0.6;

  const accWidth = (e: NEvent) => {
    const inf = infos.get(e)!;
    const cols = accColumns(e, inf);
    return cols.width;
  };
  const leftOf = (t: string) => {
    let w = 0;
    for (const e of byTime.get(t) ?? []) {
      const inf = infos.get(e)!;
      let l = accWidth(e) + (inf.side.some((s) => s < 0) ? HEAD_W : 0);
      const gr = graceBefore.get(`${t}|${e.staff}.${e.voice}`);
      if (gr) l += gr.length * 1.35 + gr.reduce((a, g) => a + accColumns(g, infos.get(g)!).width * 0.7, 0) + 0.3;
      w = Math.max(w, l);
    }
    return w;
  };
  const rightOf = (t: string) => {
    let w = HEAD_W;
    for (const e of byTime.get(t) ?? []) {
      const inf = infos.get(e)!;
      let r = e.value === 'w' || e.value === 'W' ? WHOLE_W : HEAD_W;
      if (inf.side.some((s) => s > 0)) r += HEAD_W;
      if (e.dots) r += 0.5 + e.dots * 0.5;
      if (!e.rest && !inf.beamed && valueFlags[e.value] && inf.stem < 0) r += 0.9;
      w = Math.max(w, r + (inf.shift || 0));
    }
    return w;
  };

  // Voices that collide on a staff: the down-stem voice moves right.
  for (const [k, list] of byTime) {
    const byStaff = new Map<number, NEvent[]>();
    for (const e of list) {
      if (e.rest) continue;
      const l = byStaff.get(e.staff) ?? [];
      l.push(e);
      byStaff.set(e.staff, l);
    }
    for (const l of byStaff.values()) {
      const ups = l.filter((e) => infos.get(e)!.stem < 0);
      const downs = l.filter((e) => infos.get(e)!.stem > 0);
      for (const d of downs) {
        const clash = ups.some((u) =>
          u.pitches.some((p) =>
            d.pitches.some((q) => {
              const diff = Math.abs(p.step - q.step);
              if (diff === 1) return true;
              if (diff === 0) return (u.value === 'h') !== (d.value === 'h') || u.dots !== d.dots;
              return false;
            }),
          ),
        );
        if (clash) infos.get(d)!.shift = HEAD_W * 1.05;
      }
    }
    void k;
  }

  const endRepeats = new Set(n.barlines.filter((b) => b.code === ':|').map((b) => b.t.toFixed(4)));
  const startPad = 1.2;
  const firstTime = n.times.length && n.times[0].t <= (n.measures[0]?.start ?? 0) + 1e-6 ? n.times[0] : null;
  const colX = new Map<string, number>();
  const barX = new Map<string, number>();
  const extraAt = new Map<string, Array<{ kind: 'key' | 'time' | 'clef'; x: number; data: unknown }>>();
  let x = startPad + (firstTime ? timeSigW(firstTime.value) + 0.8 : 0);
  if (firstTime) extraAt.set((n.measures[0]?.start ?? 0).toFixed(4) + 'init', [{ kind: 'time', x: startPad, data: firstTime }]);
  let prevT: number | null = null;
  let prevKey: string | null = null;
  let lastFifths = keyAt(0);
  for (const t of times) {
    const k = t.toFixed(4);
    const left = leftOf(k);
    if (prevT === null) {
      x += left;
      colX.set(k, x);
      prevT = t;
      prevKey = k;
      continue;
    }
    const d = t - prevT;
    const right = rightOf(prevKey!);
    if (measureStarts.has(k) || Math.abs(t - end) < 1e-6) {
      let gap = Math.max(spacing(d) * 0.8, right + 0.9);
      const changes = clefChanges.get(k);
      const bx0 = colX.get(prevKey!)! + gap;
      let bx = bx0;
      const extras: Array<{ kind: 'key' | 'time' | 'clef'; x: number; data: unknown }> = [];
      if (changes && !(Math.abs(t - end) < 1e-6)) {
        extras.push({ kind: 'clef', x: bx - 0.2, data: changes });
        // Last note, clef, then the repeat dots and the barline.
        bx += endRepeats.has(k) ? 3.6 : 2.2;
      } else if (endRepeats.has(k)) bx += 1.0;
      barX.set(k, bx);
      let after = bx + 1.1;
      const kc = keyChanges.get(k);
      if (kc) {
        extras.push({ kind: 'key', x: after, data: { from: lastFifths, to: kc.fifths } });
        after += keySigW(lastFifths, kc.fifths) + 0.6;
        lastFifths = kc.fifths;
      }
      const tc = timeChanges.get(k);
      if (tc && t > (n.measures[0]?.start ?? 0) + 1e-6) {
        extras.push({ kind: 'time', x: after + 0.2, data: tc });
        after += timeSigW(tc.value) + 0.9;
      }
      if (extras.length) extraAt.set(k, extras);
      x = after + left + 0.2;
      gap = x - colX.get(prevKey!)!;
      void gap;
    } else {
      x = colX.get(prevKey!)! + Math.max(spacing(d), right + left + 0.45);
      const changes = clefChanges.get(k);
      if (changes) {
        extraAt.set(k, [{ kind: 'clef', x: x - left - 2.2, data: changes }]);
        x += 2.2;
      }
    }
    colX.set(k, x);
    prevT = t;
    prevKey = k;
  }
  const width = (colX.get(end.toFixed(4)) ?? x) + 2;

  /* ---------- Drawing ---------- */
  const chunks = new Map<number, Chunk>();
  const at = (cx: number): Chunk => {
    const i = Math.floor(cx / CHUNK);
    let c = chunks.get(i);
    if (!c) {
      c = { fill: [], eo: [], hair: [], raw: [], text: [] };
      chunks.set(i, c);
    }
    return c;
  };
  const heads: HeadMark[] = [];
  const line = (x0: number, y0: number, x1: number, y1: number) => `M${r(x0)},${r(y0)}L${r(x1)},${r(y1)}`;

  // Staff lines, chunk by chunk.
  const nChunks = Math.ceil(width / CHUNK);
  for (let i = 0; i < nChunks; i++) {
    const c = at(i * CHUNK);
    const x0 = i * CHUNK;
    const x1 = Math.min(width, x0 + CHUNK);
    for (const top of staffTop) for (let l = 0; l < 5; l++) c.hair.push(line(x0, top + l, x1, top + l));
  }

  const yTop = staffTop[0];
  const yBottom = staffTop[staves - 1] + 4;
  // Barlines, repeats, bar numbers.
  const codes = new Map<string, string[]>();
  for (const b of n.barlines) {
    const k = b.t.toFixed(4);
    const l = codes.get(k) ?? [];
    l.push(b.code);
    codes.set(k, l);
  }
  for (const m of [...n.measures.slice(1).map((mm) => mm.start), end]) {
    const k = m.toFixed(4);
    const bx = barX.get(k);
    if (bx === undefined) continue;
    const cs = codes.get(k) ?? [];
    const c = at(bx);
    const final = Math.abs(m - end) < 1e-6;
    const endRepeat = cs.includes(':|');
    const startRepeat = cs.includes('|:') && !final;
    const isKeyChange = keyChanges.has(k);
    if (final || cs.includes('|.') || endRepeat) {
      c.hair.push(line(bx - 0.75, yTop, bx - 0.75, yBottom));
      c.fill.push(rect(bx - 0.4, yTop, bx + 0.1, yBottom));
      if (startRepeat) c.hair.push(line(bx + 0.45, yTop, bx + 0.45, yBottom));
    } else if (startRepeat) {
      c.fill.push(rect(bx - 0.25, yTop, bx + 0.25, yBottom));
      c.hair.push(line(bx + 0.6, yTop, bx + 0.6, yBottom));
    } else if (cs.includes('||') || isKeyChange) {
      c.hair.push(line(bx - 0.4, yTop, bx - 0.4, yBottom));
      c.hair.push(line(bx, yTop, bx, yBottom));
    } else {
      c.hair.push(line(bx, yTop, bx, yBottom));
    }
    for (const top of staffTop) {
      if (endRepeat) {
        c.fill.push(circle(bx - 1.25, top + 1.5, 0.22));
        c.fill.push(circle(bx - 1.25, top + 2.5, 0.22));
      }
      if (startRepeat) {
        const dx = endRepeat || final ? 1.0 : 1.05;
        c.fill.push(circle(bx + dx, top + 1.5, 0.22));
        c.fill.push(circle(bx + dx, top + 2.5, 0.22));
      }
    }
    const meas = n.measures.find((mm) => Math.abs(mm.start - m) < 1e-6);
    if (meas && meas.bar > 0) c.text.push({ x: bx + 0.25, y: yTop - 1.9, s: String(meas.bar), cls: 'eng-num' });
  }
  if (n.measures.length && n.measures[0].bar > 1) {
    // A piece that starts mid-way: its first bar number too.
  }

  // Key, time and clef changes.
  const drawTime = (cx: number, v: string) => {
    for (const top of staffTop) {
      const c = at(cx);
      if (v === 'C' || v === 'C|') {
        c.text.push({ x: cx + 0.9, y: top + 2.95, s: 'C', cls: 'eng-time' });
        if (v === 'C|') c.hair.push(line(cx + 0.95, top - 0.6, cx + 0.95, top + 4.6));
      } else {
        const [a, b] = v.split('/');
        c.text.push({ x: cx + timeSigW(v) / 2 - 0.15, y: top + 1.95, s: a, cls: 'eng-time' });
        c.text.push({ x: cx + timeSigW(v) / 2 - 0.15, y: top + 3.95, s: b, cls: 'eng-time' });
      }
    }
  };
  const drawKey = (cx: number, from: number, to: number, t: number) => {
    for (let s = 1; s <= staves; s++) {
      const clef = clefAt(s, t);
      const c = at(cx);
      let xx = cx + 0.5;
      if (to === 0 || Math.sign(from) !== Math.sign(to) || Math.abs(to) < Math.abs(from)) {
        // Naturals cancel what the new signature does not keep.
        const cancel = keySignatureSteps(from, clef).slice(to === 0 || Math.sign(from) !== Math.sign(to) ? 0 : Math.abs(to));
        for (const [step] of cancel) {
          const g = accidental(0, xx, yOf(s, step, clef));
          c.fill.push(g.fill);
          xx += 0.8;
        }
      }
      for (const [step, alter] of keySignatureSteps(to, clef)) {
        const g = accidental(alter, xx, yOf(s, step, clef));
        if (g.fill) c.fill.push(g.fill);
        if (g.eo) c.eo.push(g.eo);
        xx += 1.0;
      }
    }
  };
  const drawSmallClef = (cx: number, staff: number, clef: Clef) => {
    const c = at(cx);
    const top = staffTop[staff - 1];
    const k = 0.75;
    if (clef === 'G' || clef === 'G8') {
      c.raw.push(`<g class="eng-pen" transform="translate(${r(cx)},${r(top + 3 * (1 - k))}) scale(${k})"><path d="${TREBLE_CLEF}" stroke-width="0.24"/><circle cx="${TREBLE_BALL[0]}" cy="${TREBLE_BALL[1]}" r="${TREBLE_BALL[2]}"/></g>`);
    } else if (clef === 'F') {
      c.raw.push(`<g class="eng-pen" transform="translate(${r(cx)},${r(top + 1 * (1 - k))}) scale(${k})"><path d="${BASS_CLEF}" stroke-width="0.28"/>${BASS_DOTS.map(([a, b, rr]) => `<circle cx="${a}" cy="${b}" r="${rr}"/>`).join('')}</g>`);
    } else {
      c.fill.push(cClef(cx, top + (clef === 'C3' ? 2 : 1)));
    }
  };
  for (const [k, extras] of extraAt) {
    for (const ex of extras) {
      if (ex.kind === 'time') drawTime(ex.x, (ex.data as { value: string }).value);
      else if (ex.kind === 'key') {
        const d = ex.data as { from: number; to: number };
        drawKey(ex.x, d.from, d.to, Number(k.replace('init', '')));
      } else if (ex.kind === 'clef') for (const ch of ex.data as Array<{ staff: number; clef: Clef }>) drawSmallClef(ex.x, ch.staff, ch.clef);
    }
  }

  // Notes, rests, stems, flags, dots, ledgers, accidentals.
  const xOfEvent = (e: NEvent) => colX.get(e.t.toFixed(4)) ?? 0;
  const pendingRests: Array<{ e: NEvent; inf: Info; cx: number; top: number }> = [];
  for (const e of n.events) {
    const inf = infos.get(e)!;
    const top = staffTop[e.staff - 1];
    if (e.grace) continue;
    let cx = xOfEvent(e) + inf.shift;
    if (e.rest) {
      pendingRests.push({ e, inf, cx, top });
      continue;
    }
    inf.x = cx;
    const whole = e.value === 'w' || e.value === 'W';
    const hollow = whole || e.value === 'h';
    const hw = whole ? WHOLE_W : HEAD_W;
    const c = at(cx);
    let headD = '';
    e.pitches.forEach((_, i) => {
      const hx = cx + (inf.side[i] > 0 ? HEAD_W - 0.12 : inf.side[i] < 0 ? -HEAD_W + 0.12 : 0);
      const y = inf.ys[i];
      headD += whole ? wholeHead(hx, y) : hollow ? halfHead(hx, y) : blackHead(hx, y);
    });
    if (hollow) c.eo.push(headD);
    else c.fill.push(headD);
    for (const y of inf.ys) {
      track(e.staff, y + 0.5);
      track(e.staff, y - 0.5);
    }
    heads.push({ t: e.t, end: e.t + e.dur, d: headD, eo: hollow });
    // Ledger lines
    const clef = clefAt(e.staff, e.t);
    const bottom = BOTTOM[clef];
    const topStep = bottom + 8;
    const minX = cx - (inf.side.some((s) => s < 0) ? HEAD_W - 0.12 : 0) - 0.38;
    const maxX = cx + hw + (inf.side.some((s) => s > 0) ? HEAD_W - 0.12 : 0) + 0.38;
    const hi = Math.max(...e.pitches.map((p) => p.step));
    const lo = Math.min(...e.pitches.map((p) => p.step));
    for (let s = topStep + 2; s <= hi; s += 2) c.hair.push(line(minX, yOf(e.staff, s, clef), maxX, yOf(e.staff, s, clef)));
    for (let s = bottom - 2; s >= lo; s -= 2) c.hair.push(line(minX, yOf(e.staff, s, clef), maxX, yOf(e.staff, s, clef)));
    // Accidentals
    const cols = accColumns(e, inf);
    cols.items.forEach((it) => {
      const ax = cx - (inf.side.some((s) => s < 0) ? HEAD_W - 0.1 : 0) - 0.22 - it.offset - ACC_W[it.alter] / 2;
      const g = accidental(it.alter, ax, it.y);
      if (g.fill) c.fill.push(g.fill);
      if (g.eo) c.eo.push(g.eo);
    });
    // Dots
    if (e.dots) {
      const dx = cx + hw + (inf.side.some((s) => s > 0) ? HEAD_W : 0) + 0.45 + (!inf.beamed && valueFlags[e.value] && inf.stem < 0 ? 0.75 : 0);
      // One dot per note, each in a space next to its note: line notes prefer the space above
      // (below for a lower voice), taking the other side when that space is already dotted.
      const used = new Set<number>();
      [...e.pitches].sort((a, b) => b.step - a.step).forEach((p) => {
        const onLine = (((p.step - bottom) % 2) + 2) % 2 === 0;
        const y0 = yOf(e.staff, p.step, clef);
        const prefer = inf.multi && inf.stem > 0 ? 0.5 : -0.5;
        const options = onLine ? [y0 + prefer, y0 - prefer] : [y0, y0 + 1, y0 - 1];
        const y = options.find((yy) => !used.has(Math.round(yy * 2))) ?? options[0];
        used.add(Math.round(y * 2));
        for (let d = 0; d < e.dots; d++) c.fill.push(circle(dx + d * 0.55, y, 0.19));
      });
    }
    // Staccato: a dot by the notehead on the side away from the stem, in a space.
    if (e.staccato) {
      const outer = inf.stem < 0 ? Math.max(...inf.ys) : Math.min(...inf.ys);
      const s = inf.stem < 0 ? 1 : -1;
      let sy = outer + s * 1.0;
      if (sy > top - 0.01 && sy < top + 4.01 && Math.abs(sy - Math.round(sy)) < 0.01) sy += s * 0.5;
      c.fill.push(circle(cx + hw / 2, sy, 0.2));
      track(e.staff, sy);
    }
    // Fermata
    // A chorale shows its fermatas once per system: over the soprano and under the bass.
    if (e.fermata && (!opts.satb || (e.staff === 1 && e.voice === 1) || (e.staff === staves && e.voice === 2))) {
      const above = opts.satb ? e.staff === 1 : !(inf.multi && inf.stem > 0);
      const fx = cx + hw / 2;
      if (above) {
        const yMin = Math.min(...inf.ys, inf.stem < 0 ? Math.min(...inf.ys) - 3.5 : Infinity);
        c.fill.push(fermataPath(fx, Math.min(top - 0.9, yMin - 0.9), true));
      } else {
        const yMax = Math.max(...inf.ys, inf.stem > 0 ? Math.max(...inf.ys) + 3.5 : -Infinity);
        c.fill.push(fermataPath(fx, Math.max(top + 4.9, yMax + 0.9), false));
      }
    }
  }

  // Stems and flags of unbeamed notes.
  for (const e of n.events) {
    const inf = infos.get(e)!;
    if (e.rest || e.grace || !inf.stem || inf.beamed) continue;
    const flags = valueFlags[e.value] ?? 0;
    const len = 3.5 + Math.max(0, flags - 2) * 0.5;
    const m = mid(e.staff);
    const c = at(inf.x);
    if (inf.stem < 0) {
      const sx = inf.x + HEAD_W - 0.06;
      const from = Math.max(...inf.ys);
      let tip = Math.min(...inf.ys) - len;
      if (tip > m) tip = m;
      c.hair.push(line(sx, from - 0.1, sx, tip));
      track(e.staff, tip);
      for (let i = 0; i < flags; i++) c.fill.push(flagPath(sx, tip + i * 0.75, true));
      inf.tip = tip;
      inf.stemX = sx;
    } else {
      const sx = inf.x + 0.06;
      const from = Math.min(...inf.ys);
      let tip = Math.max(...inf.ys) + len;
      if (tip < m) tip = m;
      c.hair.push(line(sx, from + 0.1, sx, tip));
      track(e.staff, tip);
      for (let i = 0; i < flags; i++) c.fill.push(flagPath(sx, tip - i * 0.75, false));
      inf.tip = tip;
      inf.stemX = sx;
    }
  }

  // Beams.
  for (const g of groups) {
    const notes = g.filter((x) => !x.rest);
    const infs = notes.map((x) => infos.get(x)!);
    const dir = infs[0].stem;
    const levels = Math.max(...notes.map((x) => x.beams.length), 1);
    const len = Math.max(3.5, 2.75 + 0.75 * levels);
    const sx = infs.map((inf) => (dir < 0 ? inf.x + HEAD_W - 0.06 : inf.x + 0.06));
    const outer = infs.map((inf) => (dir < 0 ? Math.min(...inf.ys) : Math.max(...inf.ys)));
    const inner = infs.map((inf) => (dir < 0 ? Math.max(...inf.ys) : Math.min(...inf.ys)));
    const staff = notes[0].staff;
    const m = mid(staff);
    // Slope from the outer notes, limited; flat for equal ends, concave shapes and repeated figures.
    const first = outer[0];
    const lastY = outer[outer.length - 1];
    const span = sx[sx.length - 1] - sx[0];
    let slope = 0;
    const interval = Math.abs(lastY - first);
    const concave = outer.slice(1, -1).some((y) => (dir < 0 ? y < Math.min(first, lastY) - 1e-6 : y > Math.max(first, lastY) + 1e-6));
    if (interval > 1e-6 && !concave && span > 0) {
      const rise = interval <= 0.5 ? 0.25 : interval <= 1 ? 0.5 : interval <= 2 ? 0.75 : 1.0;
      slope = (Math.sign(lastY - first) * Math.min(rise, notes.length === 2 ? 1.5 : 1.0)) / span;
    }
    const target = outer.map((y) => {
      let tgt = dir < 0 ? y - len : y + len;
      if (dir < 0 && tgt > m) tgt = m;
      if (dir > 0 && tgt < m) tgt = m;
      return tgt;
    });
    let c0 = dir < 0 ? Infinity : -Infinity;
    target.forEach((tg, i) => {
      const v = tg - slope * (sx[i] - sx[0]);
      c0 = dir < 0 ? Math.min(c0, v) : Math.max(c0, v);
    });
    const yAt = (xx: number) => c0 + slope * (xx - sx[0]);
    const ch = at(sx[0]);
    infs.forEach((inf, i) => {
      ch.hair.push(line(sx[i], inner[i] + (dir < 0 ? -0.1 : 0.1), sx[i], yAt(sx[i])));
      inf.tip = yAt(sx[i]);
      inf.stemX = sx[i];
      track(staff, inf.tip);
      track(staff, inf.tip + (dir < 0 ? 1 : -1) * (0.5 + (levels - 1) * 0.75));
    });
    const inward = dir < 0 ? 1 : -1;
    for (let L = 0; L < levels; L++) {
      const off = L * 0.75 * inward;
      let segStart: number | null = null;
      let b2 = '';
      notes.forEach((x, i) => {
        const b = x.beams[L] ?? (L === 0 ? 'c' : '');
        if (b === 'b') segStart = i;
        else if (b === 'e' && segStart !== null) {
          ch.fill.push(beamPath(sx[segStart] - (dir > 0 ? 0.06 : 0), yAt(sx[segStart]) + off, sx[i] + (dir < 0 ? 0.06 : 0), yAt(sx[i]) + off, inward));
          segStart = null;
        } else if (b === 'f' || b === 'k') {
          // A stub points to the note it belongs with: back after a dotted note or at the end of the group.
          if (i === notes.length - 1 || (i > 0 && notes[i - 1].dots > 0)) b2 = 'k';
          else if (i === 0) b2 = 'f';
          else b2 = b;
          const hook = Math.min(1.1, span / Math.max(1, notes.length - 1) * 0.6);
          const x0 = b2 === 'f' ? sx[i] : sx[i] - hook;
          const x1 = b2 === 'f' ? sx[i] + hook : sx[i];
          ch.fill.push(beamPath(x0, yAt(x0) + off, x1, yAt(x1) + off, inward));
        }
      });
      if (L === 0 && segStart === null && !notes.some((x) => x.beams[0] === 'b')) {
        ch.fill.push(beamPath(sx[0], yAt(sx[0]), sx[sx.length - 1], yAt(sx[sx.length - 1]), inward));
      }
    }
  }

  // Grace notes: small, stem up, slashed when the edition says.
  for (const [key, graces] of graceBefore) {
    const [tk] = key.split('|');
    const mainX = colX.get(tk);
    if (mainX === undefined) continue;
    const mainEvent = (byTime.get(tk) ?? []).find((e) => `${e.staff}.${e.voice}` === key.split('|')[1]);
    const mainAcc = mainEvent ? accWidth(mainEvent) : 0;
    const k = 0.63;
    graces.forEach((gEv, gi) => {
      const inf = infos.get(gEv)!;
      const gx = mainX - mainAcc - 0.35 - (graces.length - gi) * 1.35;
      const c = at(gx);
      let d = '';
      inf.ys.forEach((y) => {
        d += scaledHead(gx, y, k);
      });
      c.fill.push(d);
      const sx = gx + HEAD_W * k - 0.04;
      const tip = Math.min(...inf.ys) - 2.4;
      c.hair.push(line(sx, Math.max(...inf.ys), sx, tip));
      const nflags = valueFlags[gEv.value] ?? 1;
      if (graces.length === 1 || gi === graces.length - 1 || true) {
        if (graces.length === 1) for (let i = 0; i < nflags; i++) c.fill.push(scaledFlag(sx, tip + i * 0.5, k));
      }
      if (gEv.slash) c.hair.push(line(sx - 0.55, tip + 1.3, sx + 0.6, tip + 0.4));
      // A slur from the (last) grace note to its main note, under the heads.
      if (gi === graces.length - 1 && mainEvent && !mainEvent.rest) {
        const my = Math.max(...infos.get(mainEvent)!.ys);
        const gy = Math.max(...inf.ys);
        c.fill.push(tiePath(gx + 0.2, gy + 0.55, mainX + 0.35, my + 0.7, 0.55));
      }
      inf.accs.forEach((a, i) => {
        if (a === null) return;
        const ax = gx - 0.55;
        const ay = inf.ys[i];
        const g = accidental(a, ax, ay);
        c.raw.push(`<g transform="translate(${r(ax)},${r(ay)}) scale(${k}) translate(${r(-ax)},${r(-ay)})">${g.fill ? `<path class="eng-fill" d="${g.fill}"/>` : ''}${g.eo ? `<path class="eng-fill" fill-rule="evenodd" d="${g.eo}"/>` : ''}</g>`);
      });
      const clef = clefAt(gEv.staff, gEv.t);
      const bottom = BOTTOM[clef];
      for (const p of gEv.pitches) {
        for (let s = bottom + 10; s <= p.step; s += 2) c.hair.push(line(gx - 0.25, yOf(gEv.staff, s, clef), gx + HEAD_W * k + 0.25, yOf(gEv.staff, s, clef)));
        for (let s = bottom - 2; s >= p.step; s -= 2) c.hair.push(line(gx - 0.25, yOf(gEv.staff, s, clef), gx + HEAD_W * k + 0.25, yOf(gEv.staff, s, clef)));
      }
    });
    // Several grace notes are beamed together.
    if (graces.length > 1) {
      const xs = graces.map((_, gi) => mainX - mainAcc - 0.35 - (graces.length - gi) * 1.35 + HEAD_W * k - 0.04);
      const tip = Math.min(...graces.flatMap((g) => infos.get(g)!.ys)) - 2.4;
      const c = at(xs[0]);
      const levels = valueFlags[graces[0].value] ?? 1;
      for (let L = 0; L < levels; L++) c.fill.push(beamPath(xs[0], tip + L * 0.5, xs[xs.length - 1], tip + L * 0.5, 1, 0.32));
      // The stems reach the shared beam.
      graces.forEach((g, gi) => c.hair.push(line(xs[gi], Math.min(...infos.get(g)!.ys), xs[gi], tip)));
    }
  }

  // Ties.
  for (const list of streams.values()) {
    const notes = list.filter((e) => !e.grace && !e.rest);
    notes.forEach((e, idx) => {
      const inf = infos.get(e)!;
      e.pitches.forEach((p, i) => {
        if (!p.tie) return;
        const next = notes.slice(idx + 1).find((x) => x.pitches.some((q) => q.midi === p.midi));
        const x0 = inf.x + (e.value === 'w' ? WHOLE_W : HEAD_W) + 0.12;
        const y = inf.ys[i];
        const nChord = e.pitches.length;
        const order = [...e.pitches].sort((a, b) => b.step - a.step).indexOf(p);
        // Two voices on a staff: the upper voice ties above, the lower below. One voice: away from the stem.
        const up = nChord > 1 && !inf.multi ? order < nChord / 2 : inf.multi ? inf.stem < 0 : inf.stem > 0 || (inf.stem === 0 && y < mid(e.staff));
        const s = up ? -1 : 1;
        let x1: number;
        if (next) {
          const ninf = infos.get(next)!;
          x1 = ninf.x - 0.12;
        } else x1 = x0 + 2.2;
        if (x1 - x0 < 0.6) x1 = x0 + 0.6;
        const h = s * Math.min(1.1, 0.45 + (x1 - x0) * 0.04);
        at(x0).fill.push(tiePath(x0, y + s * 0.45, x1, y + s * 0.45, h));
      });
    });
  }

  // Tuplets.
  for (const list of streams.values()) {
    let start: number | null = null;
    const evs = list.filter((e) => !e.grace);
    evs.forEach((e, i) => {
      if (e.tupletStart) start = i;
      if (e.tupletStop && start !== null) {
        const span = evs.slice(start, i + 1);
        start = null;
        const first = span[0];
        if (first.noNumber || !first.tuplet) return;
        const infs = span.map((x) => infos.get(x)!);
        const notes = infs.filter((x) => !x.e.rest);
        const dir = notes.length ? notes[0].stem || 1 : -1;
        const xs = span.map((x) => xOfEvent(x) + HEAD_W / 2);
        const xm = (xs[0] + xs[xs.length - 1]) / 2;
        const top = staffTop[first.staff - 1];
        let y: number;
        if (dir < 0) y = Math.min(top - 0.6, ...notes.map((x) => (x.tip ? x.tip : Math.min(...x.ys) - 3))) - 0.9;
        else y = Math.max(top + 4.6, ...notes.map((x) => (x.tip ? x.tip : Math.max(...x.ys) + 3))) + 1.9;
        const c = at(xs[0]);
        c.text.push({ x: xm, y, s: String(first.tuplet[0]), cls: 'eng-tuplet' });
        track(first.staff, y + 0.2);
        track(first.staff, y - 1.2);
        const g = groupOf.get(span.find((x) => !x.rest) ?? span[0]);
        const beamedAll = g && span.every((x) => x.rest || g.includes(x));
        if (first.bracket || !beamedAll) {
          const by = dir < 0 ? y - 0.55 : y - 1.0;
          const hook = dir < 0 ? 0.5 : -0.5;
          c.hair.push(line(xs[0] - 0.6, by + hook, xs[0] - 0.6, by) + line(xs[0] - 0.6, by, xm - 0.7, by));
          c.hair.push(line(xm + 0.7, by, xs[xs.length - 1] + 0.6, by) + line(xs[xs.length - 1] + 0.6, by, xs[xs.length - 1] + 0.6, by + hook));
        }
      }
    });
  }

  const cols = times.filter((t) => byTime.has(t.toFixed(4))).map((t) => ({ t, x: colX.get(t.toFixed(4))! + HEAD_W / 2 }));
  if (!cols.length || cols[0].t > 1e-6) cols.unshift({ t: n.measures[0]?.start ?? 0, x: Math.max(0, (cols[0]?.x ?? 2) - 2) });
  cols.push({ t: end, x: colX.get(end.toFixed(4)) ?? width - 2 });
  heads.sort((a, b) => a.t - b.t);
  for (const r0 of pendingRests) drawRest(r0.e, r0.inf, r0.cx, r0.top);
  return { width, height, staves, staffTop, cols, chunks, heads, clefAt, keyAt, end, reach };

  /* ---------- helpers that need the closure ---------- */
  function drawRest(e: NEvent, inf: Info, cx: number, top: number) {
    const c = at(cx);
    const two = (restVoices.get(`${e.m}|${e.staff}`)?.size ?? 0) > 1;
    const offset = two ? (e.voice % 2 === 1 ? -2 : 2) : 0;
    const clef = clefAt(e.staff, e.t);
    let y = e.restStep !== null ? yOf(e.staff, e.restStep, clef) : top + 2 + offset;
    // With two voices, a rest moves away from the other voice's notes and stems until it clears them.
    if (two && !e.measureRest) {
      // Away from the other voice: up for the voice whose stems go up in this bar (by voice number otherwise).
      const mates = n.events.filter((o) => o.m === e.m && o.staff === e.staff && o.voice === e.voice && !o.rest && !o.grace);
      const dirs = mates.map((o) => infos.get(o)!.stem).filter(Boolean);
      const away = dirs.length ? (dirs.filter((d) => d < 0).length * 2 >= dirs.length ? -1 : 1) : e.voice % 2 === 1 ? -1 : 1;
      const half = e.value === 'w' || e.value === 'W' || e.value === 'h' ? 0.6 : e.value === 'q' ? 1.5 : 1.0 + 0.5 * (valueFlags[e.value] ?? 1);
      const others: Array<[number, number]> = [];
      for (const o of byTime.get(e.t.toFixed(4)) ?? []) {
        if (o === e || o.staff !== e.staff || o.voice === e.voice || o.rest) continue;
        const oi = infos.get(o)!;
        const lo = Math.min(...oi.ys, oi.tip || Infinity) - 0.5;
        const hi = Math.max(...oi.ys, oi.tip || -Infinity) + 0.5;
        others.push([lo, hi]);
      }
      // Notes of the other voice still sounding over this rest count too (heads only).
      for (const o of n.events) {
        if (o.staff !== e.staff || o.voice === e.voice || o.rest || o.grace) continue;
        if (o.t < e.t - 1e-6 && o.t + o.dur > e.t + 1e-6) {
          const oi = infos.get(o)!;
          if (Math.abs(oi.x - cx) < 2.5) others.push([Math.min(...oi.ys) - 0.5, Math.max(...oi.ys) + 0.5]);
        }
        if (o.t > e.t + 4) break;
      }
      for (let k = 0; k < 10 && others.some(([lo, hi]) => y + half > lo && y - half < hi); k++) y += away;
    }
    if (e.measureRest || (e.value === 'w' && e.t <= n.measures[e.m].start + 1e-6 && e.dur >= n.measures[e.m].length - 1e-6)) {
      // A whole-bar rest sits in the middle of the bar.
      const mStart = n.measures[e.m].start;
      const x0 = barX.get(mStart.toFixed(4)) ?? colX.get(mStart.toFixed(4)) ?? cx;
      const nextStart = (mStart + n.measures[e.m].length).toFixed(4);
      const x1 = barX.get(nextStart) ?? colX.get(nextStart) ?? cx + 6;
      const xm = (x0 + x1) / 2;
      const ly = e.restStep !== null ? y : top + 1 + offset;
      c.fill.push(wholeRest(xm, ly));
      if (ly < top - 1e-6 || ly > top + 4 + 1e-6) c.hair.push(line(xm - 1.0, ly, xm + 1.0, ly));
      return;
    }
    const rx = cx + HEAD_W / 2;
    switch (e.value) {
      case 'W':
      case 'w': {
        const ly = e.restStep !== null || two ? y - 1 : top + 1 + offset;
        c.fill.push(wholeRest(rx, ly));
        if (ly < top - 1e-6 || ly > top + 4 + 1e-6) c.hair.push(line(rx - 1.0, ly, rx + 1.0, ly));
        break;
      }
      case 'h': {
        const ly = e.restStep !== null || two ? y : top + 2 + offset;
        c.fill.push(halfRest(rx, ly));
        if (ly < top - 1e-6 || ly > top + 4 + 1e-6) c.hair.push(line(rx - 1.0, ly, rx + 1.0, ly));
        break;
      }
      case 'q':
        c.fill.push(quarterRest(rx, y));
        break;
      default:
        y = e.restStep !== null ? y : top + 2 + offset;
        c.fill.push(hookRest(rx, y, valueFlags[e.value] ?? 1));
    }
    if (e.dots) for (let d = 0; d < e.dots; d++) c.fill.push(circle(rx + 1.0 + d * 0.55, (e.value === 'q' ? y - 0.5 : y - 0.5), 0.19));
    if (e.fermata) c.fill.push(fermataPath(rx, top - 0.9, true));
    void inf;
  }

  function accColumns(e: NEvent, inf: Info): { width: number; items: Array<{ alter: number; y: number; offset: number }> } {
    const list = e.pitches
      .map((p: NPitch, i) => ({ alter: inf.accs[i], y: inf.ys[i], step: p.step }))
      .filter((a): a is { alter: number; y: number; step: number } => a.alter !== null)
      .sort((a, b) => b.step - a.step);
    if (!list.length) return { width: 0, items: [] };
    // Columns: top first, then bottom, inward; accidentals a seventh or more apart share a column.
    const order: typeof list = [];
    let i = 0;
    let j = list.length - 1;
    while (i <= j) {
      order.push(list[i++]);
      if (i <= j) order.push(list[j--]);
    }
    const columns: Array<Array<{ step: number }>> = [];
    const items: Array<{ alter: number; y: number; offset: number }> = [];
    const colX: number[] = [];
    for (const a of order) {
      let k = 0;
      while (columns[k] && columns[k].some((o) => Math.abs(o.step - a.step) < 6)) k++;
      if (!columns[k]) columns[k] = [];
      columns[k].push(a);
      const w = ACC_W[a.alter] ?? 1;
      if (colX[k] === undefined) colX[k] = (k === 0 ? 0 : colX[k - 1] + 1.05);
      items.push({ alter: a.alter, y: a.y, offset: colX[k] + (w - 1) * 0 });
    }
    const width = colX[colX.length - 1] + Math.max(...order.map((a) => ACC_W[a.alter] ?? 1)) + 0.3;
    return { width, items };
  }
}

function r(n: number): string {
  return (Math.round(n * 1000) / 1000).toString();
}

function scaledHead(x: number, y: number, k: number): string {
  const cx = x + (HEAD_W * k) / 2;
  const a = 0.62 * k;
  const b = 0.43 * k;
  const rad = (-20 * Math.PI) / 180;
  const dx = a * Math.cos(rad);
  const dy = a * Math.sin(rad);
  return `M${r(cx - dx)},${r(y - dy)}A${r(a)},${r(b)} -20 1,1 ${r(cx + dx)},${r(y + dy)}A${r(a)},${r(b)} -20 1,1 ${r(cx - dx)},${r(y - dy)}Z`;
}

function scaledFlag(x: number, y: number, k: number): string {
  return scaleAround(flagPath(x, y, true), x, y, k);
}

/** Scale a path's coordinates about (ox, oy). Only absolute commands with plain number pairs are used by the glyphs. */
function scaleAround(d: string, ox: number, oy: number, k: number): string {
  return d.replace(/(-?\d*\.?\d+),(-?\d*\.?\d+)/g, (_, a, b) => `${r(ox + (Number(a) - ox) * k)},${r(oy + (Number(b) - oy) * k)}`);
}

export { poly };
